/**
 * StoryViewersModal — admin-only modal listing every subscriber who
 * viewed a given story. Fetches from GET /api/posts/:id/views (admin
 * gated server-side). Click a row → 📧 Push opens the PushEmailModal
 * pre-targeted at that subscriber via onPushSubscriber callback.
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import './StoryViewersModal.css';
import { useModalA11y } from '../../hooks/useModalA11y';
import { postsAPI } from '../../services/api';

export interface StoryViewer {
  viewer_user_id: string;
  viewed_at: string;
  last_viewed_at: string;
  view_count: number;
  display_name: string;
  username: string;
  avatar: string;
  email: string;
}

interface Props {
  storyId: string;
  storyCaption?: string;
  onClose: () => void;
  onPushSubscriber?: (viewer: StoryViewer) => void;
}

const StoryViewersModal: React.FC<Props> = ({ storyId, storyCaption, onClose, onPushSubscriber }) => {
  const dialogRef = useModalA11y({ onEscape: onClose });
  const [viewers, setViewers] = useState<StoryViewer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await postsAPI.getViews(storyId);
        if (cancelled) return;
        setViewers(res?.viewers || []);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || 'Failed to load viewers');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [storyId]);

  const formatRelative = (iso: string): string => {
    const t = Date.parse(iso);
    if (Number.isNaN(t)) return iso;
    const diff = Date.now() - t;
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  };

  return createPortal((
    <div
      className="modal-overlay story-viewers-overlay"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Story viewers"
      tabIndex={-1}
    >
      <div className="story-viewers-modal">
        <div className="story-viewers-header">
          <div>
            <h2>Story viewers</h2>
            {storyCaption ? <p className="story-viewers-sub">{storyCaption}</p> : null}
          </div>
          <button type="button" className="story-viewers-close" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div className="story-viewers-stat">
          {loading
            ? <span>Loading…</span>
            : <span><strong>{viewers.length}</strong> {viewers.length === 1 ? 'viewer' : 'viewers'}</span>
          }
        </div>

        <div className="story-viewers-list">
          {error && (
            <div className="story-viewers-error">{error}</div>
          )}
          {!loading && !error && viewers.length === 0 && (
            <div className="story-viewers-empty">
              <span>👁️</span>
              <p>No views yet on this story.</p>
            </div>
          )}
          {viewers.map(v => (
            <div key={v.viewer_user_id} className="story-viewer-row">
              <img
                className="story-viewer-avatar"
                src={v.avatar || '/default-avatar.png'}
                alt=""
                onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/default-avatar.png'; }}
              />
              <div className="story-viewer-meta">
                <div className="story-viewer-name">{v.display_name || v.username || 'Anonymous'}</div>
                <div className="story-viewer-time">
                  Last seen {formatRelative(v.last_viewed_at)}
                  {v.view_count > 1 && <span className="story-viewer-replays"> · {v.view_count} replays</span>}
                </div>
              </div>
              {onPushSubscriber && (
                <button
                  type="button"
                  className="story-viewer-push"
                  onClick={() => onPushSubscriber(v)}
                  title="Push email to this viewer"
                >
                  📧 Push
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  ), document.body);
};

export default StoryViewersModal;
